import {
  Actor,
  Bracing,
  Buff,
  Casting,
  Dashing,
  Falling,
  Hazard,
  Instability,
  Launch,
  Pickup,
  PlayerTag,
  Projectile,
  Renderable,
  Shockwave,
  Stunned,
  Transform,
  Velocity,
  VortexField,
} from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import { angleDelta } from '@/core/math/mathUtils'
import { BRACE, VORTEX } from '@/game/abilities/definitions'
import { ViewerResource } from '@/game/resources'
import type { CharacterVisualState, SceneBridge } from '@/gfx/SceneBridge'

/**
 * The only system that writes to the scene graph.
 *
 * Transforms are blended between the previous and current fixed step using
 * `alpha`, which is what lets a 60 Hz simulation look native on a 144 Hz
 * display. Objects whose entity died since the last frame are reaped here too,
 * so nothing else in the codebase has to think about scene lifetimes.
 */
export function createRenderSyncSystem(bridge: SceneBridge): SystemFn {
  const seen = new Set<number>()
  const visual: CharacterVisualState = {
    speed: 0,
    gait: 0,
    angle: 0,
    instability: 0,
    bracing: false,
    braceFraction: 0,
    dashing: false,
    stunned: false,
    casting: -1,
    castPhase: null,
    falling: false,
    airborne: false,
    launchSpeed: 0,
    bounces: 0,
    shield: 0,
    haste: 0,
    lookX: 0,
    lookZ: 0,
    own: false,
  }

  return ({ world, alpha, dt }) => {
    seen.clear()
    const marked = world.tryResource(ViewerResource)?.marked ?? null

    for (const e of world.query([Renderable, Transform]).entities()) {
      const renderable = world.read(e, Renderable)
      if (renderable.objectId < 0) continue
      seen.add(renderable.objectId)

      const tr = world.read(e, Transform)
      const x = tr.prevX + (tr.x - tr.prevX) * alpha
      const z = tr.prevZ + (tr.z - tr.prevZ) * alpha
      const y = tr.prevY + (tr.y - tr.prevY) * alpha
      // Interpolating angles through the shortest arc, or a character spinning
      // past ±π would whip around a full turn in one frame.
      const angle = tr.prevAngle + angleDelta(tr.prevAngle, tr.angle) * alpha

      bridge.setTransform(renderable.objectId, x, y + renderable.yOffset, z, angle)
      bridge.setVisible(renderable.objectId, renderable.visible)

      const actor = world.get(e, Actor)
      if (actor) {
        const vel = world.read(e, Velocity)
        visual.speed = Math.hypot(vel.x, vel.z)
        visual.gait = actor.gait
        visual.angle = angle
        visual.instability = world.get(e, Instability)?.value ?? 0
        const brace = world.get(e, Bracing)
        visual.bracing = brace !== undefined
        visual.braceFraction = brace ? brace.timeLeft / BRACE.maxHold : 0
        visual.dashing = world.has(e, Dashing)
        visual.stunned = world.has(e, Stunned)
        visual.falling = world.has(e, Falling)
        const cast = world.get(e, Casting)
        visual.casting = cast?.ability ?? -1
        visual.castPhase = cast?.phase ?? null
        const launch = world.get(e, Launch)
        visual.airborne = launch !== undefined
        visual.launchSpeed = launch?.speed ?? 0
        visual.bounces = launch?.bounces ?? 0
        const buff = world.get(e, Buff)
        visual.shield = buff?.shield ?? 0
        visual.haste = buff?.haste ?? 0
        visual.own = marked !== null && world.get(e, PlayerTag)?.player === marked
        nearestOpponent(world, e, tr, visual)
        bridge.updateCharacter(renderable.objectId, visual, dt)
        continue
      }

      const wave = world.get(e, Shockwave)
      if (wave) {
        bridge.updateEffect(
          renderable.objectId,
          wave.radius / wave.maxRadius,
          wave.radius,
          dt,
        )
        continue
      }

      const vortex = world.get(e, VortexField)
      if (vortex) {
        bridge.updateEffect(
          renderable.objectId,
          vortexProgress(vortex),
          vortexIntensity(vortex),
          dt,
        )
        continue
      }

      const hazard = world.get(e, Hazard)
      if (hazard) {
        const charge =
          hazard.interval > 0
            ? Math.min(1, hazard.timer / Math.max(hazard.interval, 0.001))
            : 0
        bridge.updateEffect(renderable.objectId, charge, hazard.flash, dt)
        continue
      }

      const pickup = world.get(e, Pickup)
      if (pickup) {
        bridge.updateEffect(renderable.objectId, pickup.age, 1, dt)
        continue
      }

      // The bolt animates itself (spin, pulse, its own light); without the
      // call it would fly as a still sphere.
      const projectile = world.get(e, Projectile)
      if (projectile) {
        bridge.updateEffect(renderable.objectId, projectile.life, 1, dt)
      }
    }

    // Anything the renderer holds that no living entity claimed this frame is
    // reaped here — including objects that were created and destroyed inside a
    // single fixed step, which the old "remember what you saw" bookkeeping
    // could never notice. See `Renderer.sweep`.
    bridge.sweep(seen)
  }
}

/**
 * Fills in the direction of the nearest other fighter, so a rig can look at
 * them. A head that tracks an opponent is the cheapest signal there is that a
 * character is paying attention to something.
 */
function nearestOpponent(
  world: Parameters<SystemFn>[0]['world'],
  self: number,
  from: { x: number; z: number },
  visual: CharacterVisualState,
): void {
  let bestDist = Infinity
  visual.lookX = 0
  visual.lookZ = 0
  for (const other of world.query([Actor, Transform]).entities()) {
    if (other === self || world.has(other, Falling)) continue
    const t = world.read(other, Transform)
    const dx = t.x - from.x
    const dz = t.z - from.z
    const dist = Math.hypot(dx, dz)
    if (dist >= bestDist || dist < 1e-3) continue
    bestDist = dist
    visual.lookX = dx / dist
    visual.lookZ = dz / dist
  }
}

function vortexProgress(field: ReturnType<typeof VortexField.create>): number {
  switch (field.phase) {
    case 'arming':
      return 1 - field.timer / VORTEX.arming
    case 'pulling':
      return 1
    default:
      return 1
  }
}

/** Drives brightness: fades in while arming, blazes on the burst. */
function vortexIntensity(field: ReturnType<typeof VortexField.create>): number {
  switch (field.phase) {
    case 'arming':
      return 0.25 + 0.45 * (1 - field.timer / VORTEX.arming)
    case 'pulling':
      return 0.8 + 0.2 * Math.sin(field.timer * 14)
    default:
      return Math.max(0, field.timer / 0.45) * 1.6
  }
}
